<template>
  <transition
    name="unpin-slide"
    @after-leave="afterLeave"
  >
    <div v-if="!isLeaving" class="info-card" :class="{ expanded: showFooter }">
      <div class="info-card-content">
        <!-- Header row: icon + title + pin -->
        <div class="document-header">
          <div class="icon-container">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="16" viewBox="0 0 12 16" fill="none">
              <path d="M3 8.8H9V7.2H3V8.8ZM3 11.2H9V9.6H3V11.2ZM3 13.6H6.75V12H3V13.6ZM1.5 16C1.0875 16 0.734375 15.8433 0.440625 15.53C0.146875 15.2167 0 14.84 0 14.4V1.6C0 1.16 0.146875 0.783333 0.440625 0.47C0.734375 0.156667 1.0875 0 1.5 0H7.5L12 4.8V14.4C12 14.84 11.8531 15.2167 11.5594 15.53C11.2656 15.8433 10.9125 16 10.5 16H1.5ZM6.75 5.6V1.6H1.5V14.4H10.5V5.6H6.75Z" fill="#25B8C2"/>
            </svg>
          </div>
          <p class="info-text-title">Document:</p>
          <div class="header-action-icons">
            <div v-if="!isCardPinned" class="action-icon" @click.stop="handlePinCard">
              <img src="@/assets/cardActions/pin.svg" alt="Pin" />
            </div>
            <div v-if="isCardPinned" class="action-icon" @click.stop="startUnpin">
              <img src="@/assets/cardActions/unpin.svg" alt="Unpin" />
            </div>
          </div>
        </div>

        <!-- Content: documents list -->
        <div class="document-content">
          <div v-if="displayLoading" class="loading-state">
            <p class="info-text">Loading documents...</p>
          </div>
          <div v-else-if="displayDocuments.length > 0" class="documents-list">
            <div
              v-for="doc in displayDocuments"
              :key="doc.id"
              class="document-item"
              @click="handleDocumentClick(doc)"
            >
              <div class="document-main-info">
                <div class="document-title-row">
                  <p class="document-title">{{ doc.title || doc.fileName || 'Untitled' }}</p>
                </div>
                <div class="document-meta-row">
                  <span class="document-date">{{ formatDate(doc.createdAt) }}</span>
                  <span class="document-file-info">{{ formatFileInfo(doc) }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <p class="info-text">There are no documents found for this patient.</p>
          </div>
        </div>
      </div>
      <div class="card-expand" :class="{ 'justify-end': !showFooter, 'justify-between': showFooter }">
        <FoldableButton v-if="showFooter" customized_class="drag-handle" text="Move">
          <template #prefix>
            <img src="@/assets/cardActions/move.svg" alt="Move" />
          </template>
        </FoldableButton>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useCardActions } from '@/composables/useCardActions'
import { usePatientInfoStore } from '@/store/helpers/my-patients/patient-info'
import { downloadPatientFileApi } from '@/api/patientFiles'

const props = defineProps({
  patient: {
    type: String,
    default: ''
  },
  cardData: {
    type: Object,
    default: () => ({})
  },
  showFooter: {
    type: Boolean,
    default: true
  },
  context: {
    type: String,
    default: 'pegboard'
  },
  patientId: {
    type: [String, Number],
    required: true
  },
  documents: {
    type: Array,
    default: undefined
  },
  isLoading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['pin-card', 'unpin-card'])

const patientInfoStore = usePatientInfoStore()
const { isCardPinned, handlePinCard, handleUnpinCard } = useCardActions(props, emit)

const isLeaving = ref(false)

const displayDocuments = computed(() => {
  if (props.documents && props.documents.length > 0) {
    return props.documents
  }
  return patientInfoStore.state.currentPatientDocuments.value || []
})

const displayLoading = computed(() => {
  return props.isLoading || patientInfoStore.state.documentsLoading.value || false
})

const startUnpin = () => {
  if (props.showFooter) {
    isLeaving.value = true
  } else {
    handleUnpinCard()
  }
}

const afterLeave = () => {
  emit('unpin-card', props.cardData)
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A'
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return 'N/A'
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const month = months[date.getMonth()]
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()
    return `${month}-${day}-${year}`
  } catch (e) {
    return 'N/A'
  }
}

const formatFileSize = (bytes) => {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}

const formatFileType = (contentType) => {
  if (!contentType) return ''
  const typeMap = {
    'application/pdf': 'PDF',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.ms-excel': 'XLS',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'text/plain': 'TXT',
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/gif': 'GIF'
  }
  return typeMap[contentType] || contentType.split('/').pop().toUpperCase()
}

const formatFileInfo = (doc) => {
  const type = formatFileType(doc.contentType || doc.mime_type)
  const size = formatFileSize(doc.fileSize || doc.size_bytes)
  return [type, size].filter(Boolean).join(' ')
}

const handleDocumentClick = async (doc) => {
  // For returned forms, open external URL
  if (doc.type === 'returned_form' && (doc.externalUrl || doc.external_url)) {
    window.open(doc.externalUrl || doc.external_url, '_blank')
    return
  }

  // For report type, skip download (no blob available)
  if (doc.type === 'report') {
    return
  }

  // Download file blob
  try {
    const response = await downloadPatientFileApi(doc.id)
    const blob = new Blob([response.data], { type: doc.contentType || doc.mime_type || 'application/octet-stream' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = doc.title || doc.fileName || doc.file_name || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Failed to download document:', error)
  }
}
</script>

<style scoped>
.unpin-slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.unpin-slide-leave-to {
  transform: translateX(-100%) scale(0.95);
  opacity: 0;
}

.info-card {
  background-color: white;
  border-radius: 5px;
  padding: 12px 16px;
  box-shadow: 0px 1px 25px 0px rgba(74, 74, 74, 0.15);
}

.info-card-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.document-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.document-header .info-text-title {
  flex: 1;
}

.icon-container {
  background-color: #f2f2f2;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}

.info-text-title {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #666;
  margin: 0;
  line-height: 24px;
}

.header-action-icons {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: auto;
}

.action-icon {
  width: 28px;
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  padding: 5px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.action-icon:hover {
  background-color: #E5F1FB;
}

.document-content {
  margin-left: 32px;
}

.loading-state,
.empty-state {
  padding: 8px 0;
  text-align: center;
  color: #666666;
  font-style: italic;
  margin-left: -32px;
}

.empty-state {
  color: #999999;
}

.info-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #333333;
  letter-spacing: 0.25px;
  line-height: 20px;
}

.documents-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 104px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
  width: 100%;
}

.documents-list::-webkit-scrollbar {
  width: 0px;
}

.documents-list:hover::-webkit-scrollbar {
  width: 4px;
}

.documents-list::-webkit-scrollbar-track {
  background: transparent;
}

.documents-list::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.documents-list::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.document-item {
  width: 100%;
  flex-shrink: 0;
  border-radius: 5px;
  background: var(--Cool-White, #F9FBFD);
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.document-item:hover {
  background: #E5F1FB;
}

.document-main-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.document-title-row {
  display: flex;
  align-items: center;
}

.document-title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #333;
  line-height: 20px;
  letter-spacing: 0.25px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.document-meta-row {
  display: flex;
  gap: 16px;
  align-items: center;
}

.document-date {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  color: #666;
  line-height: 14px;
  letter-spacing: 0.1px;
}

.document-file-info {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  color: #666;
  line-height: 14px;
  letter-spacing: 0.1px;
}

/* Hover footer */
.card-expand {
  display: flex;
  align-items: center;
  gap: 16px;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  padding-top: 0;
  margin-top: 0;
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
  border-top-color: transparent;
  pointer-events: none;
  transition: max-height 300ms linear,
              opacity 300ms linear,
              padding-top 300ms linear,
              margin-top 300ms linear,
              border-top-color 300ms linear;
}

.info-card.expanded:hover .card-expand {
  max-height: 80px;
  opacity: 1;
  padding-top: 10px;
  margin-top: 10px;
  border-top-color: var(--DividerGray, #C4C4C4);
  pointer-events: auto;
}

.expand-action {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1B79A0;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 5px;
}

.expand-action span {
  color: var(--color-Darkblue-000, #1E4670);
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
  text-decoration-line: underline;
}

.expand-action:hover {
  background-color: #E5F1FB;
}

.move-expand {
  display: flex;
  align-items: center;
  width: 26px;
  overflow: hidden;
  padding: 4px;
  justify-content: center;
  transition: width 0.4s ease, padding 0.4s ease, gap 0.4s ease;
  gap: 0;
}

.move-expand:hover {
  padding: 4px 10px;
  justify-content: flex-start;
  gap: 6px;
  width: 78px;
}

.move-expand span {
  white-space: nowrap;
  opacity: 0;
  min-width: 0;
  transition: opacity 0.3s ease;
  transition-delay: 0.1s;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  color: #1E4670;
  text-decoration: underline;
}

.move-expand:hover span {
  opacity: 1;
}

.move-expand img {
  flex-shrink: 0;
}
</style>
